/**
 * Trust Signals блок — статистика, security badges, отзывы.
 *
 * Языки: en, ru, es, it, hy.  de и fr пропущены по запросу пользователя
 * — fallback на en сработает через `translations[language] || translations.en`.
 *
 * Отзывы — реальные пользователи. Ссылки на их соцсети и URL формы для
 * комментариев заданы константами в TrustSignals.tsx (одинаковы для всех языков).
 */

export type Translations = {
    sectionTitle: string
    sectionSubtitle: string

    // 4 stat cards
    statExchangesLabel: string
    statExchangesValue: string         // "25+"
    statTradesLabel: string
    statTradesValue: string             // "100k+"
    statLanguagesLabel: string
    statLanguagesValue: string          // "6"
    statYearsLabel: string
    statYearsValue: string              // "5+"

    // Security badges
    badgesTitle: string
    badge1Title: string
    badge1Desc: string
    badge2Title: string
    badge2Desc: string
    badge3Title: string
    badge3Desc: string

    // Testimonials (2 реальных отзыва + блок-форма на месте третьего)
    testimonialsTitle: string
    testimonial1Quote: string
    testimonial1Author: string
    testimonial1Country: string
    testimonial2Quote: string
    testimonial2Author: string
    testimonial2Country: string

    // Feedback form block (3-я колонка)
    feedbackText: string                // надпись сверху
    feedbackTitle: string               // заголовок блока
    feedbackButton: string              // подпись на кнопке-ссылке
    // Живые отзывы (форма прямо на сайте вместо внешней Google-формы)
    reviewPlaceholder: string
    reviewSubmit: string
    reviewPosted: string
    reviewError: string
    reviewLoginHint: string
    reviewDelete: string
    reviewShowMore: string
    reviewTranslated: string
    reviewShowOriginal: string
    reviewAll: string
}

const translations: Record<string, Translations> = {
    en: {
        sectionTitle: 'Trusted by traders worldwide',
        sectionSubtitle:
            'Built on open infrastructure, secured by design, supported across exchanges and languages',

        statExchangesLabel: 'Supported exchanges',
        statExchangesValue: '25+',
        statTradesLabel: 'Automated trades',
        statTradesValue: '100k+',
        statLanguagesLabel: 'Languages',
        statLanguagesValue: '6',
        statYearsLabel: 'Years on the market',
        statYearsValue: '5+',

        badgesTitle: 'Security & transparency by design',
        badge1Title: 'Read & Trade only',
        badge1Desc:
            'API keys you provide never carry withdrawal rights — funds stay on the exchange under your control.',
        badge2Title: 'TronLink authentication',
        badge2Desc:
            'No password to leak. Authentication is signed by your TronLink wallet on every login.',
        badge3Title: 'Open-source RCC contract',
        badge3Desc:
            'RCC token, staking ledger and referral cascade are verifiable on-chain on Tron.',

        testimonialsTitle: 'What our users say',
        testimonial1Quote:
            'RealColibri works just great. After launch it trades on its own without a single losing deal. I never thought it could achieve such results — fantastic! A friend invited me to try this algorithm. At first I was skeptical, but then I got excellent results. Tens of percent over a few months. I did not have to think about anything. The RealColibri system does everything itself.',
        testimonial1Author: 'Emanuele V.',
        testimonial1Country: 'Italy',
        testimonial2Quote:
            'An excellent tool for automating trading. I have been using the RealColibri system for almost a year and I am happy with the results. A special thanks to the developers for quality support. I recommend it both to beginners and experienced traders.',
        testimonial2Author: 'Davit K.',
        testimonial2Country: 'Armenia',

        feedbackText: 'Leave your opinions and suggestions here',
        feedbackTitle: 'Feedback form',
        feedbackButton: 'Leave a review',
        reviewPlaceholder: 'Share your experience with the algorithm...',
        reviewSubmit: 'Post review',
        reviewPosted: 'Thank you! Your review has been published.',
        reviewError: 'Could not post the review. Please try again.',
        reviewLoginHint: 'Sign in to leave a review.',
        reviewDelete: 'Delete this review?',
        reviewShowMore: 'Show more',
        reviewTranslated: 'Translated automatically',
        reviewShowOriginal: 'show original',
        reviewAll: 'All reviews ({n})',
    },
    ru: {
        sectionTitle: 'Нам доверяют трейдеры по всему миру',
        sectionSubtitle:
            'Открытая инфраструктура, безопасность по дизайну, поддержка нескольких бирж и языков',

        statExchangesLabel: 'Поддерживаемых бирж',
        statExchangesValue: '25+',
        statTradesLabel: 'Автоматических сделок',
        statTradesValue: '100k+',
        statLanguagesLabel: 'Языков',
        statLanguagesValue: '6',
        statYearsLabel: 'Лет на рынке',
        statYearsValue: '5+',

        badgesTitle: 'Безопасность и прозрачность',
        badge1Title: 'Только Read & Trade',
        badge1Desc:
            'API-ключи, которые вы вводите, никогда не несут права на вывод средств — деньги остаются на бирже под вашим контролем.',
        badge2Title: 'Авторизация через TronLink',
        badge2Desc:
            'Нет пароля, который можно потерять. Авторизация подписывается вашим TronLink-кошельком при каждом входе.',
        badge3Title: 'Open-source контракт RCC',
        badge3Desc:
            'Токен RCC, стейкинг и реферальный каскад можно проверить on-chain в сети Tron.',

        testimonialsTitle: 'Что говорят пользователи',
        testimonial1Quote:
            'RealColibri работает просто отлично. После запуска он самостоятельно торгует без единой минусовой сделки. Я и не думал, что он способен достичь таких результатов, фантастика! Меня пригласил попробовать этот алгоритм один друг. Сначала я относился к этому скептически, но потом получил отличные результаты. Это десятки процентов за несколько месяцев. Не пришлось ни о чём думать. Система RealColibri всё делает сама.',
        testimonial1Author: 'Emanuele V.',
        testimonial1Country: 'Италия',
        testimonial2Quote:
            'Отличный инструмент для автоматизации торговли. Пользуюсь системой RealColibri уже почти год, доволен результатами. Отдельный плюс разработчикам за качественную поддержку. Рекомендую как новичкам, так и опытным трейдерам.',
        testimonial2Author: 'Давид Х.',
        testimonial2Country: 'Армения',

        feedbackText: 'Оставьте здесь ваши мнения и пожелания',
        feedbackTitle: 'Форма для комментариев',
        feedbackButton: 'Оставить отзыв',
        reviewPlaceholder: 'Поделитесь опытом работы с алгоритмом...',
        reviewSubmit: 'Отправить',
        reviewPosted: 'Спасибо! Ваш отзыв опубликован.',
        reviewError: 'Не удалось отправить отзыв. Попробуйте ещё раз.',
        reviewLoginHint: 'Войдите, чтобы оставить отзыв.',
        reviewDelete: 'Удалить этот отзыв?',
        reviewShowMore: 'Показать ещё',
        reviewTranslated: 'Переведено автоматически',
        reviewShowOriginal: 'показать оригинал',
        reviewAll: 'Все отзывы ({n})',
    },
    es: {
        sectionTitle: 'Confiado por traders de todo el mundo',
        sectionSubtitle:
            'Construido sobre infraestructura abierta, seguro por diseño, con soporte en múltiples bolsas e idiomas',

        statExchangesLabel: 'Bolsas soportadas',
        statExchangesValue: '25+',
        statTradesLabel: 'Operaciones automatizadas',
        statTradesValue: '100k+',
        statLanguagesLabel: 'Idiomas',
        statLanguagesValue: '6',
        statYearsLabel: 'Años en el mercado',
        statYearsValue: '5+',

        badgesTitle: 'Seguridad y transparencia por diseño',
        badge1Title: 'Solo Read & Trade',
        badge1Desc:
            'Las claves API que proporcionas nunca tienen derechos de retiro — los fondos permanecen en la bolsa bajo tu control.',
        badge2Title: 'Autenticación TronLink',
        badge2Desc:
            'Sin contraseñas que filtrar. La autenticación es firmada por tu billetera TronLink en cada inicio de sesión.',
        badge3Title: 'Contrato RCC de código abierto',
        badge3Desc:
            'El token RCC, el registro de staking y la cascada de referidos son verificables on-chain en Tron.',

        testimonialsTitle: 'Lo que dicen nuestros usuarios',
        testimonial1Quote:
            'RealColibri funciona de maravilla. Tras el lanzamiento opera por sí solo sin una sola operación perdedora. Nunca pensé que pudiera lograr tales resultados, ¡fantástico! Un amigo me invitó a probar este algoritmo. Al principio era escéptico, pero luego obtuve excelentes resultados. Decenas de por ciento en pocos meses. No tuve que pensar en nada. El sistema RealColibri lo hace todo solo.',
        testimonial1Author: 'Emanuele V.',
        testimonial1Country: 'Italia',
        testimonial2Quote:
            'Una herramienta excelente para automatizar el trading. Llevo casi un año usando el sistema RealColibri y estoy satisfecho con los resultados. Un plus aparte para los desarrolladores por el soporte de calidad. Lo recomiendo tanto a principiantes como a traders experimentados.',
        testimonial2Author: 'Davit K.',
        testimonial2Country: 'Armenia',

        feedbackText: 'Deja aquí tus opiniones y sugerencias',
        feedbackTitle: 'Formulario de comentarios',
        feedbackButton: 'Dejar una reseña',
        reviewPlaceholder: 'Comparte tu experiencia con el algoritmo...',
        reviewSubmit: 'Publicar',
        reviewPosted: 'Gracias! Tu resena ha sido publicada.',
        reviewError: 'No se pudo publicar la resena. Intentalo de nuevo.',
        reviewLoginHint: 'Inicia sesion para dejar una resena.',
        reviewDelete: 'Eliminar esta resena?',
        reviewShowMore: 'Mostrar mas',
        reviewTranslated: 'Traducido automáticamente',
        reviewShowOriginal: 'ver original',
        reviewAll: 'Todas las reseñas ({n})',
    },
    it: {
        sectionTitle: 'Scelto dai trader di tutto il mondo',
        sectionSubtitle:
            'Costruito su infrastruttura aperta, sicuro per design, supportato su più borse e lingue',

        statExchangesLabel: 'Borse supportate',
        statExchangesValue: '25+',
        statTradesLabel: 'Operazioni automatizzate',
        statTradesValue: '100k+',
        statLanguagesLabel: 'Lingue',
        statLanguagesValue: '6',
        statYearsLabel: 'Anni sul mercato',
        statYearsValue: '5+',

        badgesTitle: 'Sicurezza e trasparenza per design',
        badge1Title: 'Solo Read & Trade',
        badge1Desc:
            'Le chiavi API che fornisci non hanno mai diritti di prelievo — i fondi rimangono sulla borsa sotto il tuo controllo.',
        badge2Title: 'Autenticazione TronLink',
        badge2Desc:
            'Nessuna password da perdere. L\'autenticazione è firmata dal tuo wallet TronLink a ogni accesso.',
        badge3Title: 'Contratto RCC open-source',
        badge3Desc:
            'Il token RCC, il registro staking e la cascata referenze sono verificabili on-chain su Tron.',

        testimonialsTitle: 'Cosa dicono i nostri utenti',
        testimonial1Quote:
            'RealColibri funziona davvero alla grande. Dopo l\'avvio opera da solo senza una singola operazione in perdita. Non avrei mai pensato che potesse raggiungere risultati simili, fantastico! Un amico mi ha invitato a provare questo algoritmo. All\'inizio ero scettico, ma poi ho ottenuto ottimi risultati. Decine di punti percentuali in pochi mesi. Non ho dovuto pensare a nulla. Il sistema RealColibri fa tutto da solo.',
        testimonial1Author: 'Emanuele V.',
        testimonial1Country: 'Italia',
        testimonial2Quote:
            'Uno strumento eccellente per automatizzare il trading. Uso il sistema RealColibri da quasi un anno e sono soddisfatto dei risultati. Un plus a parte per gli sviluppatori per il supporto di qualità. Lo consiglio sia ai principianti che ai trader esperti.',
        testimonial2Author: 'Davit K.',
        testimonial2Country: 'Armenia',

        feedbackText: 'Lascia qui le tue opinioni e i tuoi suggerimenti',
        feedbackTitle: 'Modulo per i commenti',
        feedbackButton: 'Lascia una recensione',
        reviewPlaceholder: 'Condividi la tua esperienza con l\'algoritmo...',
        reviewSubmit: 'Pubblica',
        reviewPosted: 'Grazie! La tua recensione e stata pubblicata.',
        reviewError: 'Impossibile pubblicare la recensione. Riprova.',
        reviewLoginHint: 'Accedi per lasciare una recensione.',
        reviewDelete: 'Eliminare questa recensione?',
        reviewShowMore: 'Mostra altro',
        reviewTranslated: 'Tradotto automaticamente',
        reviewShowOriginal: 'mostra originale',
        reviewAll: 'Tutte le recensioni ({n})',
    },
    hy: {
        sectionTitle: 'Մեզ վստահում են առևտրականները ողջ աշխարհում',
        sectionSubtitle:
            'Կառուցված է բաց ենթակառուցվածքի վրա, անվտանգ է դիզայնով, աջակցվում է բազմաթիվ բորսաներով և լեզուներով',

        statExchangesLabel: 'Աջակցվող բորսաներ',
        statExchangesValue: '25+',
        statTradesLabel: 'Ավտոմատացված գործարքներ',
        statTradesValue: '100k+',
        statLanguagesLabel: 'Լեզուներ',
        statLanguagesValue: '6',
        statYearsLabel: 'Տարիներ շուկայում',
        statYearsValue: '5+',

        badgesTitle: 'Անվտանգություն և թափանցիկություն դիզայնով',
        badge1Title: 'Միայն Read & Trade',
        badge1Desc:
            'Ձեր տրամադրած API բանալիները երբեք չունեն դուրսբերման իրավունք — միջոցները մնում են բորսայում ձեր վերահսկողության տակ։',
        badge2Title: 'TronLink նույնականացում',
        badge2Desc:
            'Չկա գաղտնաբառ, որը կարող է արտահոսել։ Նույնականացումը ստորագրվում է ձեր TronLink դրամապանակով յուրաքանչյուր մուտքագրմամբ։',
        badge3Title: 'Բաց կոդով RCC պայմանագիր',
        badge3Desc:
            'RCC թոքենը, ստեյքինգի ռեեստրը և ռեֆերալային կասկադը հնարավոր է ստուգել on-chain Tron-ում։',

        testimonialsTitle: 'Ինչ են ասում մեր օգտատերերը',
        testimonial1Quote:
            'RealColibri-ն աշխատում է պարզապես հիանալի։ Գործարկումից հետո ինքնուրույն առևտուր է անում՝ առանց որևէ վնասով գործարքի։ Չէի էլ մտածում, որ ընդունակ է հասնել նման արդյունքների, ֆանտաստիկա է: Այս ալգորիթմը փորձելու ինձ հրավիրեց մի ընկեր։ Սկզբում թերահավատ էի, բայց հետո ստացա հիանալի արդյունքներ։ Տասնյակ տոկոսներ մի քանի ամսում։ Ստիպված չէի որևէ բանի մասին մտածել։ RealColibri համակարգն ամեն ինչ ինքն է անում:',
        testimonial1Author: 'Emanuele V.',
        testimonial1Country: 'Իտալիա',
        testimonial2Quote:
            'Հիանալի գործիք առևտուրն ավտոմատացնելու համար։ Օգտվում եմ RealColibri համակարգից արդեն գրեթե մեկ տարի, գոհ եմ արդյունքներից։ Առանձին շնորհակալություն մշակողներին որակյալ աջակցության համար։ Խորհուրդ եմ տալիս ինչպես սկսնակներին, այնպես էլ փորձառու առևտրականներին:',
        testimonial2Author: 'Davit K.',
        testimonial2Country: 'Հայաստան',

        feedbackText: 'Թողեք այստեղ ձեր կարծիքներն ու առաջարկները',
        feedbackTitle: 'Մեկնաբանությունների ձև',
        feedbackButton: 'Թողնել կարծիք',
        reviewPlaceholder: 'Կիսվեք ալգորիթմի հետ ձեր փորձով...',
        reviewSubmit: 'Ուղարկել',
        reviewPosted: 'Շնորհակալություն։ Ձեր կարծիքը հրապարակվել է։',
        reviewError: 'Չհաջողվեց հրապարակել կարծիքը։ Փորձեք կրկին։',
        reviewLoginHint: 'Մուտք գործեք՝ կարծիք թողնելու համար։',
        reviewDelete: 'Ջնջե՞լ այս կարծիքը։',
        reviewShowMore: 'Ցույց տալ ավելին',
        reviewTranslated: 'Ավտոմատ թարգմանված',
        reviewShowOriginal: 'ցույց տալ բնօրինակը',
        reviewAll: 'Բոլոր կարծիքները ({n})',
    },
}

export default translations
